import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { fetchTitilerPointValue } from '../utils/titilerPoint';

/**
 * Multiband CADI forecast COG — Philippines.
 * Band 1 = September 2026, band 12 = August 2027.
 */
export const CADI_STACKED_COG_URL =
  'https://digisaka.s3.amazonaws.com/agriculture_drought/CADI_stacked_forecast_202609_202708.tif';

export interface CADIDroughtFrame {
  /** ISO month id for slider labels, e.g. `2026-09` */
  date: string;
  /** Human label, e.g. `Sep 2026` */
  label: string;
  cogUrl: string;
  /** 1-based band in the stacked forecast COG */
  bidx: number;
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** First band = Sep 2026; last band = Aug 2027. */
const CADI_STACK_START = { year: 2026, month: 9 };
const CADI_STACK_BAND_COUNT = 12;

function addMonths(year: number, month: number, offset: number): { year: number; month: number } {
  const total = year * 12 + (month - 1) + offset;
  return { year: Math.floor(total / 12), month: (total % 12) + 1 };
}

export const CADI_DROUGHT_FRAMES: CADIDroughtFrame[] = Array.from(
  { length: CADI_STACK_BAND_COUNT },
  (_, i) => {
    const { year, month } = addMonths(CADI_STACK_START.year, CADI_STACK_START.month, i);
    const monthLabel = MONTH_NAMES[month - 1] ?? String(month);
    return {
      date: `${year}-${String(month).padStart(2, '0')}`,
      label: `${monthLabel} ${year}`,
      cogUrl: CADI_STACKED_COG_URL,
      bidx: i + 1,
    };
  }
);

/** Philippines extent (EPSG:4326) — from TiTiler `/api/bounds` on CADI COGs. */
export const CADI_DROUGHT_BBOX: [number, number, number, number] = [
  116.92547708138447, 4.585505709624794, 126.60452291861552, 21.124494290375203,
];

const TITILER_TILE_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';

/** CADI values are normalized 0–1; higher = greater drought stress. */
export const CADI_DROUGHT_RESCALE: [number, number] = [0, 1];

/**
 * Empty pixels outside cropland are stored as 255.
 * The GeoTIFF nodata tag is corrupt, so tiles and statistics must pass this explicitly.
 * Leaving it as NaN folds 255 into the mean and pushes municipality averages toward 200+.
 */
export const CADI_FILL_NODATA = '255';

export interface CADIDroughtClass {
  /** Inclusive lower bound. */
  min: number;
  /** Exclusive upper bound, except the last class which includes 1.0. */
  max: number;
  color: string;
  label: string;
}

/** Discrete CADI classes from the forecast colorscheme. */
export const CADI_DROUGHT_CLASSES: CADIDroughtClass[] = [
  { min: 0.0, max: 0.4, color: '#88cb5f', label: 'No drought' },
  { min: 0.4, max: 0.55, color: '#ffffcc', label: 'Dry' },
  { min: 0.55, max: 0.65, color: '#fed976', label: 'Moderate' },
  { min: 0.65, max: 0.75, color: '#fd8d3c', label: 'Severe' },
  { min: 0.75, max: 0.85, color: '#e31a1c', label: 'Extreme' },
  { min: 0.85, max: 1.0, color: '#800026', label: 'Exceptional' },
];

function hexToRgba(hex: string): [number, number, number, number] {
  const value = hex.replace('#', '');
  const n = Number.parseInt(value, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255];
}

/**
 * TiTiler interval colormap (rio-tiler): [[[min, max], [r,g,b,a]], ...].
 * Last upper bound is slightly above 1 so CADI = 1.0 is included.
 */
export const CADI_DROUGHT_INTERVAL_COLORMAP: Array<[[number, number], [number, number, number, number]]> =
  CADI_DROUGHT_CLASSES.map((cls, i) => {
    const upper = i === CADI_DROUGHT_CLASSES.length - 1 ? 1.01 : cls.max;
    return [[cls.min, upper], hexToRgba(cls.color)];
  });

export function cadiCategoryFromMean(value: number): { label: string; color: string } {
  const last = CADI_DROUGHT_CLASSES[CADI_DROUGHT_CLASSES.length - 1];
  if (!Number.isFinite(value)) return { label: last.label, color: last.color };
  for (const cls of CADI_DROUGHT_CLASSES) {
    if (value < cls.max) return { label: cls.label, color: cls.color };
  }
  return { label: last.label, color: last.color };
}

/** Layer panel / map info — how CADI is computed. */
export const CADI_DROUGHT_DESCRIPTION =
  'The Combined Agricultural Drought Index (CADI) integrates six key drought indicators: Land Surface Temperature anomaly (LST_ANOM), Vegetation Condition Index (VCI), Standardized Surface Runoff Index (SSRI), Reconnaissance Drought Index (RDI), Soil Moisture Index (SMI), and Evaporative Stress Index (ESI). These indicators represent different but related indicators of agricultural drought, including thermal stress, vegetation condition, hydrometeorological conditions, rainfall–evapotranspiration balance, soil moisture availability, and evaporative stress. By combining these indicators, CADI provides a holistic assessment of drought conditions than any single index, allowing drought intensity, development, persistence, and spatial extent to be characterized more effectively across agricultural areas.';

/** Map legend — discrete CADI classes (0 = no drought, 1 = exceptional). */
export const CADI_DROUGHT_LEGEND_DESCRIPTION =
  'Combined Agricultural Drought Index (CADI) forecast values range from 0 to 1. Classes: 0.00–0.40 No drought, 0.40–0.55 Dry, 0.55–0.65 Moderate, 0.65–0.75 Severe, 0.75–0.85 Extreme, 0.85–1.00 Exceptional. Higher CADI values represent increasing drought intensity and greater likelihood of agricultural drought occurrence.';

export const CADI_DROUGHT_LEGEND: {
  colors: string[];
  labels: string[];
  description: string;
  legendDescription: string;
  min: number;
  max: number;
  classes: CADIDroughtClass[];
} = {
  colors: CADI_DROUGHT_CLASSES.map((cls) => cls.color),
  labels: CADI_DROUGHT_CLASSES.map((cls) => cls.label),
  description: 'El Niño CADI forecast — discrete classes from no drought (green) to exceptional (dark red), 0–1',
  legendDescription: CADI_DROUGHT_LEGEND_DESCRIPTION,
  min: CADI_DROUGHT_RESCALE[0],
  max: CADI_DROUGHT_RESCALE[1],
  classes: CADI_DROUGHT_CLASSES,
};

export function buildCADIDroughtTileUrl(
  cogUrl: string = CADI_STACKED_COG_URL,
  bidx = 1
): string {
  const params = new URLSearchParams();
  params.set('url', cogUrl);
  params.set('bidx', String(bidx));
  params.set('colormap', JSON.stringify(CADI_DROUGHT_INTERVAL_COLORMAP));
  // Nearest preserves sparse cropland pixels in COG overviews; lanczos blurs them to
  // transparent at Philippines-wide zoom (z5–6) so the layer only appears when zoomed in.
  params.set('resampling', 'nearest');
  // Empty cropland pixels are stored as 255. The file nodata tag is corrupt.
  params.set('nodata', CADI_FILL_NODATA);
  params.set('format', 'png');
  return `${TITILER_TILE_BASE}?${params.toString()}`;
}

export interface CADIPointValue {
  value: number | null;
  label: string;
  color: string;
}

/** Read one CADI pixel from TiTiler `/point`. Results are cached by ~1 km cell. */
export async function fetchCADIPointValue(
  lon: number,
  lat: number,
  bidx: number,
  signal?: AbortSignal
): Promise<CADIPointValue> {
  const raw = await fetchTitilerPointValue(CADI_STACKED_COG_URL, lon, lat, {
    bidx,
    nodata: CADI_FILL_NODATA,
    signal,
  });
  const value = raw != null && raw >= 0 && raw <= 1 ? raw : null;
  const category = value == null
    ? { label: 'No data', color: '#9ca3af' }
    : cadiCategoryFromMean(value);
  return { value, label: category.label, color: category.color };
}

/** Alerts-tab CADI horizon: current calendar month + next months (4 total). */
export const CADI_ALERTS_MONTHS_AHEAD = 4;

function cadiMonthOrdinal(year: number, month: number): number {
  return year * 12 + month;
}

function parseCadiDateOrdinal(dateStr: string): number | null {
  const match = dateStr.match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || month < 1 || month > 12) return null;
  return cadiMonthOrdinal(year, month);
}

function alertsWindowOrdinals(
  monthsAhead = CADI_ALERTS_MONTHS_AHEAD,
  now = new Date()
): { start: number; end: number } {
  const start = cadiMonthOrdinal(now.getFullYear(), now.getMonth() + 1);
  return { start, end: start + monthsAhead - 1 };
}

/** Index of the forecast month matching today, or 0 if outside the stack. */
export function defaultCadiFrameIndex(now = new Date()): number {
  const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const idx = CADI_DROUGHT_FRAMES.findIndex((frame) => frame.date === key);
  return idx >= 0 ? idx : 0;
}

/** CADI frames shown in the Alerts tab (from current month, N months ahead). */
export function getCadiAlertsFrames(
  monthsAhead = CADI_ALERTS_MONTHS_AHEAD,
  now = new Date()
): CADIDroughtFrame[] {
  const { start, end } = alertsWindowOrdinals(monthsAhead, now);
  return CADI_DROUGHT_FRAMES.filter((frame) => {
    const key = parseCadiDateOrdinal(frame.date);
    return key != null && key >= start && key <= end;
  });
}

/** Keep only CADI timeseries points within the alerts-tab horizon. */
export function filterCadiPointsForAlertsTab<T extends { date: string }>(
  points: T[],
  monthsAhead = CADI_ALERTS_MONTHS_AHEAD,
  now = new Date()
): T[] {
  const { start, end } = alertsWindowOrdinals(monthsAhead, now);
  return points.filter((p) => {
    const key = parseCadiDateOrdinal(p.date);
    return key != null && key >= start && key <= end;
  });
}
